import './style.scss';

import { type FC } from 'react';

import { clockTime } from './shared';

const LockGlyph = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<rect
			x='4'
			y='10'
			width='16'
			height='11'
			rx='2'
		/>
		<path d='M8 10V7a4 4 0 0 1 8 0v3' />
	</svg>
);

/**
 * Takes the place of "Tap to take action" on a locked unit. Ruled off in the stamp's ink, like a
 * struck entry in a register: it says what is blocked, what unblocks it, and offers the only two
 * things still worth doing.
 */
export const UnitLockStrip: FC<{
	discreet?: boolean;
	hasPriorResult: boolean;
	onViewResult: () => void;
	/** Absent when the support desk is unavailable. */
	onMessageParty?: () => void;
}> = ({ discreet, hasPriorResult, onViewResult, onMessageParty }) => (
	<div className={`pc-lock${discreet ? ' pc-lock--discreet' : ''}`}>
		<div className='pc-lock__head'>
			<LockGlyph />
			<p className='pc-lock__title'>Results locked</p>
		</div>
		<p className='pc-lock__body'>
			{hasPriorResult
				? 'You sent a result before the cancellation. Nothing more can be uploaded until your party reinstates this unit.'
				: 'You can upload a result once your party reinstates this unit.'}
		</p>
		{hasPriorResult || onMessageParty ? (
			<div className='pc-lock__actions'>
				{hasPriorResult ? (
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={onViewResult}
					>
						View sent result
					</button>
				) : null}
				{onMessageParty ? (
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={onMessageParty}
					>
						INEC reversed it? Message your party
					</button>
				) : null}
			</div>
		) : null}
	</div>
);

/** Shown once a unit this phone saw locked comes back reinstated, until the agent acts on it. */
export const UnitReinstatedBanner: FC<{
	at?: string | null;
	reason?: string | null;
	canUpload: boolean;
	onUpload: () => void;
	onDismiss: () => void;
}> = ({ at, reason, canUpload, onUpload, onDismiss }) => {
	const time = clockTime(at);
	return (
		<div
			className='pc-reinstated'
			role='status'
			aria-live='polite'
		>
			<p className='pc-reinstated__title'>Your party reinstated this unit{time ? ` at ${time}` : ''}.</p>
			<p className='pc-reinstated__body'>
				{canUpload ? 'You can upload your result now.' : 'Results are unlocked again.'}
				{reason ? <span className='pc-reinstated__reason'> “{reason}”</span> : null}
			</p>
			<div className='pc-reinstated__actions'>
				{canUpload ? (
					<button
						type='button'
						className='pc-btn pc-btn--primary'
						onClick={onUpload}
					>
						Upload result
					</button>
				) : null}
				<button
					type='button'
					className='pc-btn pc-btn--ghost'
					onClick={onDismiss}
				>
					Dismiss
				</button>
			</div>
		</div>
	);
};

/** The upload panel on a locked unit: the form is not rendered at all. */
export const UnitLockedPanel: FC<{ cancelledAt?: string; onMessageParty?: () => void }> = ({ cancelledAt, onMessageParty }) => {
	const at = clockTime(cancelledAt);
	return (
		<div
			className='pc-lock pc-lock--panel'
			role='alert'
		>
			<div className='pc-lock__head'>
				<LockGlyph />
				<p className='pc-lock__title'>INEC cancelled this unit{at ? ` at ${at}` : ''}</p>
			</div>
			<p className='pc-lock__body'>
				Your party has the cancellation report, so results for this unit are locked. If INEC reverses it, ask your party to
				reinstate the unit — uploads open again straight away.
			</p>
			{onMessageParty ? (
				<div className='pc-lock__actions'>
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={onMessageParty}
					>
						Message your party
					</button>
				</div>
			) : null}
		</div>
	);
};
